// src/components/resources/tools/generators/CommentThread/threadModel.ts

/**
 * Pure data model + helpers for the comment-thread mockups, split out from the
 * `'use client'` components (mirroring `RedditPost/scoring.ts`) so the
 * username/@-handling, count formatting, and the nested-reply tree operations
 * can be unit-tested in the node test env without rendering React.
 *
 * Shared by the Comment Thread Mockup Generator (Instagram / YouTube / TikTok /
 * X) and the Reddit Post Generator's comment-thread mode.
 */

/** Platforms the Comment Thread Mockup Generator renders. */
export type CommentPlatform = 'instagram' | 'youtube' | 'tiktok' | 'x'

/**
 * One comment in a thread. `replies` nests arbitrarily deep in the model; the
 * per-surface editor caps visible depth via its capabilities (`maxDepth`) so
 * each platform stays true to its real UI (one reply level on the social
 * platforms, deeper indent rails on Reddit).
 */
export interface ThreadComment {
  /** Stable identity for editor targeting (never rendered). */
  id: string
  /**
   * Author handle as typed — MAY carry a leading `@`. Renderers must go
   * through {@link normalizeHandle} / {@link formatHandle} /
   * {@link avatarInitial} so `@user` and `user` produce identical output
   * (a leading `@` once yielded an empty avatar initial when the first-char
   * pick ran before the strip).
   */
  username: string
  text: string
  /** Like/upvote count. Non-negative on the social platforms; Reddit comment scores may be negative. */
  likes: number
  /** Free-text relative timestamp (e.g. "2h", "3 days ago"). */
  timestamp: string
  /** Verified badge (rendered only on platforms that show one in comments). */
  verified: boolean
  /**
   * Comment written by the post's author. Rendered per platform convention:
   * "Author" tag (Instagram), highlighted handle pill (YouTube), "Creator"
   * suffix (TikTok), OP badge (Reddit). X shows no author affordance, so the
   * flag is ignored there.
   */
  byAuthor: boolean
  /** Pinned to the top of the thread (top-level only; not an X affordance). */
  pinned: boolean
  /**
   * Viewer-liked state: renders the platform's filled like affordance (heart /
   * thumb) and adds +1 to the displayed count (see {@link displayedLikes}).
   * Toggleable from the editor AND by clicking the affordance in the preview.
   * Reddit layouts use {@link vote} instead.
   */
  liked: boolean
  /**
   * YouTube's "hearted by creator" badge on a comment (the small red heart on
   * a creator avatar under the like count). Ignored by the other layouts —
   * they have no equivalent affordance.
   */
  creatorHearted: boolean
  /**
   * Reddit comment-vote state (up / down / none), mirroring the post card's
   * interactive vote pill: ±1 on the displayed score with the orange/blue
   * highlight. Ignored by the social layouts (they use {@link liked}).
   */
  vote: 'up' | 'down' | null
  replies: ThreadComment[]
}

/**
 * The like count a social layout should display: the configured base count
 * plus the viewer's own like. (Reddit layouts apply `vote` via `applyVote`
 * from `RedditPost/scoring` instead.)
 */
export function displayedLikes(comment: Pick<ThreadComment, 'likes' | 'liked'>): number {
  return comment.likes + (comment.liked ? 1 : 0)
}

/** What the shared thread editor exposes for a given surface. */
export interface ThreadEditorCapabilities {
  /** Show the verified-badge toggle. */
  verified: boolean
  /** Show the author toggle, and what the rendered badge is called. */
  authorBadge: boolean
  authorBadgeLabel: string
  /** Show the pinned toggle (top-level comments only). */
  pinned: boolean
  /** Label for the likes/score input ("Likes" / "Upvotes"). */
  likesLabel: string
  /** Reddit comment scores can go negative; social likes cannot. */
  allowNegativeLikes: boolean
  /**
   * Show the "Liked" viewer-state toggle (filled heart/thumb, count +1). All
   * four social layouts render it; Reddit uses in-preview votes instead.
   */
  likedToggle: boolean
  /** Show the YouTube-only "Creator heart" toggle. */
  creatorHeart: boolean
  /** Maximum nesting depth the editor offers "Add reply" at (root = depth 0). */
  maxDepth: number
}

/**
 * Editor capabilities per social platform — each layout only exposes the
 * affordances it renders, so the controls can't stage a mockup element the
 * selected layout would silently drop:
 * - Instagram: verified badges, pinned comments, and an "Author" tag.
 * - YouTube: verified checkmark, a "Pinned by …" line, and a highlighted
 *   handle pill on creator comments.
 * - TikTok: pinned comments and a "Creator" label (no verified toggle —
 *   this layout doesn't render one).
 * - X: verified checkmarks only — this layout renders no pinned or author
 *   affordance on replies.
 */
export const PLATFORM_EDITOR_CAPABILITIES: Readonly<Record<CommentPlatform, ThreadEditorCapabilities>> = {
  instagram: {
    verified: true,
    authorBadge: true,
    authorBadgeLabel: 'Author',
    pinned: true,
    likesLabel: 'Likes',
    allowNegativeLikes: false,
    likedToggle: true,
    creatorHeart: false,
    maxDepth: 1,
  },
  youtube: {
    verified: true,
    authorBadge: true,
    authorBadgeLabel: 'Creator',
    pinned: true,
    likesLabel: 'Likes',
    allowNegativeLikes: false,
    likedToggle: true,
    creatorHeart: true,
    maxDepth: 1,
  },
  tiktok: {
    verified: false,
    authorBadge: true,
    authorBadgeLabel: 'Creator',
    pinned: true,
    likesLabel: 'Likes',
    allowNegativeLikes: false,
    likedToggle: true,
    creatorHeart: false,
    maxDepth: 1,
  },
  x: {
    verified: true,
    authorBadge: false,
    authorBadgeLabel: '',
    pinned: false,
    likesLabel: 'Likes',
    allowNegativeLikes: false,
    likedToggle: true,
    creatorHeart: false,
    maxDepth: 1,
  },
}

/** Reddit's comment-thread mode: OP badges, deeper nesting, negative scores. */
export const REDDIT_COMMENT_CAPABILITIES: ThreadEditorCapabilities = {
  verified: false,
  authorBadge: true,
  authorBadgeLabel: 'OP',
  pinned: false,
  likesLabel: 'Upvotes',
  allowNegativeLikes: true,
  // Reddit engagement is the tri-state vote (interactive in the preview,
  // mirroring the post card), not a liked toggle.
  likedToggle: false,
  creatorHeart: false,
  maxDepth: 3,
}

/**
 * Strip whitespace, ALL leading `@` characters, and a leading `u/` prefix
 * from a typed handle, so `@growthbymarco`, `growthbymarco`, and Reddit-style
 * `u/growthbymarco` all resolve to the same canonical handle. Every renderer
 * derives from this — usernames typed with or without a prefix must produce
 * identical mockups (the layouts re-add their own `@`/`u/` chrome), and the
 * author-match/rename logic compares through it too.
 */
export function normalizeHandle(raw: string): string {
  return raw.trim().replace(/^@+/, '').replace(/^u\//i, '')
}

/** Canonical `@handle` display form — exactly one leading `@`. */
export function formatHandle(raw: string): string {
  return `@${normalizeHandle(raw)}`
}

/**
 * Strip whitespace and a leading `r/` prefix (case-insensitive, exact
 * `r/` only — `random` is untouched) from a typed subreddit, mirroring
 * {@link normalizeHandle}'s `u/` handling. Every surface that renders the
 * community or derives its avatar initial must go through this, so a pasted
 * `r/marketing` and a bare `marketing` produce identical mockups — the
 * layouts re-add their own `r/` chrome (`r3564364630`).
 */
export function normalizeSubreddit(raw: string): string {
  return raw.trim().replace(/^r\//i, '')
}

/**
 * First character of the normalized handle, uppercased, for the initials
 * avatar. Normalization runs FIRST: picking the first char and then stripping
 * `@` is the order-of-operations bug that rendered an empty avatar for
 * `@`-prefixed usernames. Falls back to `'U'` for an empty/`@`-only handle.
 */
export function avatarInitial(raw: string): string {
  const normalized = normalizeHandle(raw)
  return (normalized[0] ?? 'U').toUpperCase()
}

/**
 * Compact engagement count (1280 → "1.2K", 2_400_000 → "2.4M"), matching the
 * uppercase-suffix convention Instagram/YouTube/TikTok/X use on comment
 * counters. Drops a trailing ".0" (2000 → "2K") and promotes across a unit
 * boundary after rounding (999_950 → "1M", not "1000K"). Negative values
 * (Reddit comment scores) keep their sign.
 */
export function formatCompactCount(value: number): string {
  if (value < 0) return `-${formatCompactCount(-value)}`
  const unit = (n: number, divisor: number, suffix: string): string =>
    `${(n / divisor).toFixed(1).replace(/\.0$/, '')}${suffix}`
  if (value >= 1_000_000_000) return unit(value, 1_000_000_000, 'B')
  if (value >= 1_000_000) {
    return Number((value / 1_000_000).toFixed(1)) >= 1000 ? unit(value, 1_000_000_000, 'B') : unit(value, 1_000_000, 'M')
  }
  if (value >= 1_000) {
    return Number((value / 1_000).toFixed(1)) >= 1000 ? unit(value, 1_000_000, 'M') : unit(value, 1_000, 'K')
  }
  return value.toString()
}

/**
 * Ceiling on total comments (nested replies included) the editors will add.
 * Every comment is a live editor row + preview row; an unbounded list turns
 * the deferred preview re-render into a long task on mobile. 30 is far beyond
 * what a legible mockup screenshot fits.
 */
export const MAX_THREAD_COMMENTS = 30

let idSeq = 0

/**
 * Fresh unique id for a new comment. A module counter (not `Math.random`) so
 * ids are stable within a session and cheap; the prefix keeps them readable in
 * React DevTools. Never rendered or persisted.
 */
export function createCommentId(): string {
  idSeq += 1
  return `tc-${idSeq}`
}

/**
 * Build a comment with safe defaults; override any field.
 *
 * Pass an explicit `id` for DEFAULT trees rendered during SSR: the module
 * counter advances per process, so a long-lived server process would emit
 * `tc-7…` into the HTML while the fresh client module hydrates `tc-1…`,
 * breaking the editor's id/`aria-controls` pairing. Deterministic ids per
 * initializer call keep server and client output identical; the counter is
 * only for comments created from client-side interactions (post-hydration).
 */
export function makeComment(overrides: Partial<ThreadComment> = {}): ThreadComment {
  return {
    id: createCommentId(),
    username: '',
    text: '',
    likes: 0,
    timestamp: 'now',
    verified: false,
    byAuthor: false,
    pinned: false,
    liked: false,
    creatorHearted: false,
    vote: null,
    replies: [],
    ...overrides,
  }
}

/**
 * Immutably patch the comment with `id` anywhere in the tree. Returns a new
 * array; untouched subtrees keep referential identity so memoized preview rows
 * can bail out. No-op (same content) if the id is absent.
 */
export function updateCommentById(
  comments: readonly ThreadComment[],
  id: string,
  patch: Partial<Omit<ThreadComment, 'id' | 'replies'>>,
): ThreadComment[] {
  return comments.map(comment => {
    if (comment.id === id) return { ...comment, ...patch }
    const replies = updateCommentById(comment.replies, id, patch)
    return replies === comment.replies || replies.every((r, i) => r === comment.replies[i])
      ? comment
      : { ...comment, replies }
  })
}

/**
 * Immutably append `reply` to the `replies` of the comment with `parentId`,
 * at any depth. Returns the input content unchanged if the parent is absent.
 */
export function addReplyById(
  comments: readonly ThreadComment[],
  parentId: string,
  reply: ThreadComment,
): ThreadComment[] {
  return comments.map(comment => {
    if (comment.id === parentId) return { ...comment, replies: [...comment.replies, reply] }
    const replies = addReplyById(comment.replies, parentId, reply)
    return replies.every((r, i) => r === comment.replies[i]) ? comment : { ...comment, replies }
  })
}

/**
 * Immutably rename every author comment (`byAuthor: true`) to `newAuthor`,
 * at any depth. `byAuthor` MEANS "written by the post author", so when the
 * creator handle changes, the author-badged comments' handles follow —
 * otherwise a mockup can export one brand's creator handle in the header with
 * another brand's name on the author-badged comments.
 */
export function renameAuthorComments(comments: readonly ThreadComment[], newAuthor: string): ThreadComment[] {
  return comments.map(comment => {
    const replies = renameAuthorComments(comment.replies, newAuthor)
    const repliesChanged = !replies.every((r, i) => r === comment.replies[i])
    if (comment.byAuthor) return { ...comment, username: newAuthor, replies }
    return repliesChanged ? { ...comment, replies } : comment
  })
}

/** Immutably flip the viewer-liked state of the comment with `id`, anywhere in the tree. */
export function toggleLikedById(comments: readonly ThreadComment[], id: string): ThreadComment[] {
  return comments.map(comment => {
    if (comment.id === id) return { ...comment, liked: !comment.liked }
    const replies = toggleLikedById(comment.replies, id)
    return replies.every((r, i) => r === comment.replies[i]) ? comment : { ...comment, replies }
  })
}

/**
 * Immutably toggle the tri-state Reddit vote on the comment with `id`,
 * anywhere in the tree: same direction again clears the vote; the other
 * direction switches it. The toggle derives from the comment's CURRENT vote
 * in the tree being updated — callers run this inside a functional state
 * updater so a rapid second click can't compute "next" from a stale rendered
 * (deferred) snapshot and send the same direction twice (`r3564364629`).
 */
export function toggleVoteById(
  comments: readonly ThreadComment[],
  id: string,
  direction: 'up' | 'down',
): ThreadComment[] {
  return comments.map(comment => {
    if (comment.id === id) return { ...comment, vote: comment.vote === direction ? null : direction }
    const replies = toggleVoteById(comment.replies, id, direction)
    return replies.every((r, i) => r === comment.replies[i]) ? comment : { ...comment, replies }
  })
}

/** Immutably remove the comment with `id` (and its nested replies) from anywhere in the tree. */
export function removeCommentById(comments: readonly ThreadComment[], id: string): ThreadComment[] {
  return comments
    .filter(comment => comment.id !== id)
    .map(comment => {
      const replies = removeCommentById(comment.replies, id)
      return replies.length === comment.replies.length && replies.every((r, i) => r === comment.replies[i])
        ? comment
        : { ...comment, replies }
    })
}

/** Total comment count including every nested reply. */
export function countComments(comments: readonly ThreadComment[]): number {
  return comments.reduce((sum, comment) => sum + 1 + countComments(comment.replies), 0)
}

/**
 * Thread ordering for render: pinned top-level comments first (stable within
 * each group). Only top-level comments pin — platforms don't pin replies.
 */
export function orderThread(comments: readonly ThreadComment[]): ThreadComment[] {
  const pinned = comments.filter(c => c.pinned)
  const rest = comments.filter(c => !c.pinned)
  return [...pinned, ...rest]
}
